import { api } from './api';

import { AccountAddressDto, UserDto } from 'api/dtos/user';
import {
    AuthResponse,
    LoginRequest,
    RegisterRequest,
    UserResponse,
} from 'api/dtos/auth';
import { CreateEntityDto } from 'api/dtos/entity';
import { InvoiceDetailsUpsertDto } from 'api/dtos/invoiceDetailsDtos';
import { PaymentDetailsDto } from 'api/dtos/subscriptionPlanDtos';

export interface UpdateProfileRequest {
    firstName?: string;
    lastName?: string;
    newEmail?: string;
    address?: AccountAddressDto;
}

export interface RequestPasswordChangeRequest {
    currentPassword: string;
    newPassword: string;
}

export interface ConfirmPasswordChangeRequest {
    code: string;
}

export interface ApiMessageResponse {
    message: string;
}

export interface RequestEmailChangeDto {
    newEmail: string;
}

export interface ConfirmEmailChangeDto {
    code: string;
}

export interface RequestPasswordResetRequest {
    email: string;
}

export interface ResendActivationEmailRequest {
    email: string;
}

export interface ConfirmPasswordResetRequest {
    email: string;
    code: string;
    newPassword: string;
}

export interface InitialSetupParishBookPersonRequest {
    firstName: string;
    lastName: string;
    role: string;
    availability?: string;
    description?: string;
    phoneNumber?: string;
    email?: string;
    visibleForGuests: boolean;
}

export interface InitialSetupParishBookCategoryRequest {
    name: string;
    persons?: InitialSetupParishBookPersonRequest[];
}

export interface InitialSetupRequest {
    paymentDetails?: PaymentDetailsDto;
    address: AccountAddressDto;
    invoiceDetails?: InvoiceDetailsUpsertDto;
    subscriptionPlanId: string;
    entity?: CreateEntityDto;
    parishBookCategories?: InitialSetupParishBookCategoryRequest[];
}

export const accountApi = api.injectEndpoints({
    endpoints: (builder) => ({
        requestPasswordReset: builder.mutation<{ message: string }, RequestPasswordResetRequest>({
            query: (body) => ({
                url: '/Account/request-password-reset',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage: 'Nie udało się wysłać kodu. Spróbuj ponownie.',
                successMessage: 'Kod weryfikacyjny został wysłany na podany adres e-mail.',
            },
        }),
        confirmPasswordReset: builder.mutation<{ message: string }, ConfirmPasswordResetRequest>({
            query: (body) => ({
                url: '/Account/confirm-password-reset',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage: 'Resetowanie hasła nie powiodło się. Sprawdź kod i spróbuj ponownie.',
                successMessage: 'Hasło zostało pomyślnie zresetowane!',
            },
        }),

        login: builder.mutation<AuthResponse, LoginRequest>({
            query: (body) => ({
                url: '/Account/login',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Account'],
        }),
        getUserData: builder.query<UserDto, void>({
            query: () => ({
                url: '/Account/me',
                method: 'GET',
            }),
            providesTags: ['Account'],
            transformResponse: (response: UserResponse) => response.user,
        }),
        activate: builder.query<void, { userId: string; token: string }>({
            query: ({ userId, token }) => ({
                url: '/Account/activate',
                method: 'GET',
                params: { userId, token },
            }),
        }),
        resendActivationEmail: builder.mutation<{ message: string }, ResendActivationEmailRequest>({
            query: (body) => ({
                url: '/Account/resend-activation-email',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage: 'Nie udało się wysłać wiadomości aktywacyjnej. Spróbuj ponownie.',
                successMessage: 'Wiadomość aktywacyjna została wysłana ponownie.',
            },
        }),
        register: builder.mutation<AuthResponse, RegisterRequest>({
            query: (body) => ({
                url: '/Account/register',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage:
                    'Rejestracja nie powiodła się. Sprawdź podane dane i spróbuj ponownie.',
                successMessage: 'Pomyślnie zarejestrowano!',
            },
        }),
        initialSetup: builder.mutation<void, InitialSetupRequest>({
            query: (body) => ({
                url: '/Account/initial-setup',
                method: 'POST',
                body,
            }),
            invalidatesTags: [
                'Account',
                'Entity',
                'Subscription',
                'PaymentMethods',
                'ParishBookCategory',
                'ParishBookPerson',
            ],
        }),
        updateProfile: builder.mutation<
            { message: string },
            UpdateProfileRequest
        >({
            query: (body) => ({
                url: '/Account/update-profile',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Account'],
        }),
        confirmEmailChangeByLink: builder.query<
            string,
            { userId: string; newEmail: string; token: string }
        >({
            query: ({ userId, newEmail, token }) => ({
                url: '/Account/confirm-email-change',
                method: 'GET',
                params: { userId, newEmail, token },
            }),
        }),
        requestEmailChange: builder.mutation<ApiMessageResponse, RequestEmailChangeDto>({
            query: (body) => ({
                url: '/Account/request-email-change',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage: 'Nie udało się wysłać kodu zmiany adresu e-mail. Spróbuj ponownie.',
                successMessage: 'Kod potwierdzający zmianę adresu e-mail został wysłany.',
            },
        }),
        confirmEmailChange: builder.mutation<ApiMessageResponse, ConfirmEmailChangeDto>({
            query: (body) => ({
                url: '/Account/confirm-email-change',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Account'],
            extraOptions: {
                errorMessage: 'Nie udało się potwierdzić zmiany adresu e-mail. Sprawdź kod i spróbuj ponownie.',
                successMessage: 'Adres e-mail został zmieniony.',
            },
        }),
        requestPasswordChange: builder.mutation<
            { message: string },
            RequestPasswordChangeRequest
        >({
            query: (body) => ({
                url: '/Account/request-password-change',
                method: 'POST',
                body,
            }),
            extraOptions: {
                errorMessage: 'Zmiana hasła nie powiodła się. Sprawdź podane dane i spróbuj ponownie.',
                successMessage: 'Pomyślnie zmieniono hasło!',
            },
        }),
        confirmPasswordChange: builder.mutation<
            string,
            ConfirmPasswordChangeRequest
        >({
            query: (body) => ({
                url: '/Account/confirm-password-change',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Account'],
        }),
        refreshToken: builder.mutation<AuthResponse, void>({
            query: () => ({
                url: '/Account/refresh-token',
                method: 'POST',
            }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useLoginMutation,
    useResendActivationEmailMutation,
    useRegisterMutation,
    useLazyActivateQuery,
    useActivateQuery,
    useGetUserDataQuery,
    useUpdateProfileMutation,
    useConfirmEmailChangeByLinkQuery,
    useLazyConfirmEmailChangeByLinkQuery,
    useRequestEmailChangeMutation,
    useConfirmEmailChangeMutation,
    useRequestPasswordChangeMutation,
    useConfirmPasswordChangeMutation,
    useRequestPasswordResetMutation,
    useConfirmPasswordResetMutation,
    useRefreshTokenMutation,
    useInitialSetupMutation,
} = accountApi;
